import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
  CalendarClock, FileText, PartyPopper, Contact, Sparkles, ShieldCheck,
  Camera, Wand2, Check,
} from 'lucide-react';

export default function LandingScreen() {
  const navigate = useNavigate();

  // Native iOS / Android users already installed the app — skip the marketing
  // landing and drop them straight into the app. Web visitors still see it.
  useEffect(() => {
    if (Capacitor?.isNativePlatform?.()) {
      navigate('/app', { replace: true });
    }
  }, [navigate]);

  return (
    <div className="landing">
      {/* Top bar */}
      <header className="landing__topbar">
        <div className="landing__brand">
          <span className="landing__logo" aria-hidden="true">
            <svg viewBox="0 0 64 64" width="28" height="28">
              <rect width="64" height="64" rx="14" fill="#2563eb"/>
              <path d="M16 22 L22 22 L22 16 M48 22 L42 22 L42 16 M16 42 L22 42 L22 48 M48 42 L42 42 L42 48"
                stroke="white" strokeWidth="3" strokeLinecap="round" fill="none"/>
              <rect x="22" y="26" width="20" height="14" rx="2" fill="none" stroke="white" strokeWidth="2.5"/>
              <circle cx="32" cy="33" r="3" fill="white"/>
            </svg>
          </span>
          <span className="landing__brand-name">ScanRemind</span>
        </div>
        <button className="btn btn--primary" onClick={() => navigate('/app')}>
          Open app
        </button>
      </header>

      {/* Hero */}
      <section className="landing__hero">
        <span className="landing__eyebrow">Outlook-first · No signup · Nothing stored</span>
        <h1 className="landing__title">
          Snap a photo.<br />
          It's on your calendar.
        </h1>
        <p className="landing__subtitle">
          Doctor appointment cards, bills, event flyers, business cards — point your camera,
          confirm the details, and they land in your Microsoft 365 calendar or contacts.
          No typing dates off paper, ever again.
        </p>
        <div className="landing__cta">
          <button className="btn btn--primary" onClick={() => navigate('/app')}>
            Try it free
          </button>
          <a className="btn btn--ghost" href="#how-it-works">How it works</a>
        </div>
        <p className="landing__cta-sub">No credit card. No account. Just connect Microsoft once.</p>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="landing__section">
        <h2 className="landing__section-title">Three taps</h2>
        <div className="landing__steps">
          <StepCard num="1" Icon={Camera} title="Snap it"
            body="Point your phone at an appointment card, bill, flyer, or business card. Tap the shutter." />
          <StepCard num="2" Icon={Wand2} title="AI reads it"
            body="Claude extracts the date, time, location, contact details — whatever the category needs — in about 5 seconds." />
          <StepCard num="3" Icon={Check} title="Confirm and done"
            body="Review the fields, tweak anything, tap Add. It's in Outlook on every device you own." />
        </div>
      </section>

      {/* Features */}
      <section id="features" className="landing__section">
        <h2 className="landing__section-title">Four kinds of paper, gone in seconds</h2>
        <div className="landing__features">
          <FeatureCard Icon={CalendarClock} title="Appointments"
            body="Doctor, dentist, vet — snap the card and it's on your calendar with the address baked in." />
          <FeatureCard Icon={FileText} title="Bills"
            body="Photograph a bill and get a reminder the day before it's due, with the amount and payee." />
          <FeatureCard Icon={PartyPopper} title="Flyers & invites"
            body="Birthday parties, school events — turn paper into a calendar entry in two taps." />
          <FeatureCard Icon={Contact} title="Business cards"
            body="Conferences, networking — name, title, email, phone, address straight into Outlook contacts." />
          <FeatureCard Icon={Sparkles} title="Outlook / M365 native"
            body="Built around Microsoft 365. One Microsoft popup, not a fake account on our side." />
          <FeatureCard Icon={ShieldCheck} title="Nothing stored"
            body="No signup. No profile. No history. Your photo goes to AI, becomes an event, then vanishes." />
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="landing__section">
        <h2 className="landing__section-title">Questions, answered</h2>
        <div className="landing__faq">
          <FaqItem q="Do I need to make an account?"
            a="No. There's no signup, no password, no email collected. The only sign-in is the one-time Microsoft popup that authorizes ScanRemind to write to your Outlook calendar and contacts — same flow you use with Zoom, Slack, or any 'Continue with Microsoft' app." />
          <FaqItem q="Is my photo stored anywhere?"
            a="No. The photo travels to our AI for analysis, comes back as structured fields, and is discarded. We don't keep images, scans, or a history list. Nothing about your scans lives on our servers." />
          <FaqItem q="Does this work with Google or Apple Calendar?"
            a="Not yet. ScanRemind is Outlook-first by design. Google support is on the roadmap once we cover the verification process Google requires for calendar/contacts access." />
          <FaqItem q="Does this work on iPhone?"
            a="Yes — it's a website that works perfectly in mobile Safari. Add to Home Screen for a near-native feel. A dedicated iOS app is coming." />
          <FaqItem q="What does it cost?"
            a="Free during early access. Pricing for the public release is still being decided." />
          <FaqItem q="Who's behind this?"
            a={<>Built by <a href="https://vitalintegrators.com" target="_blank" rel="noreferrer">Vital Integrators</a>, a Lafayette, Louisiana IT and cybersecurity firm that's lived in Microsoft 365 every day for over a decade.</>} />
        </div>
      </section>

      {/* Final CTA */}
      <section className="landing__finalcta">
        <h2 className="landing__section-title" style={{ marginBottom: 8 }}>Stop typing dates off paper.</h2>
        <p className="muted" style={{ maxWidth: 520, margin: '0 auto 24px' }}>
          Connect your Microsoft account once. The next time someone hands you an appointment card, snap it and move on with your day.
        </p>
        <button className="btn btn--primary" onClick={() => navigate('/app')}>
          Try ScanRemind
        </button>
      </section>

      {/* Footer */}
      <footer className="landing__footer">
        <div>
          <strong>ScanRemind</strong> · A product of <a href="https://vitalintegrators.com" target="_blank" rel="noreferrer">Vital Integrators</a> · Lafayette, LA
        </div>
        <div className="landing__footer-links">
          <a href="mailto:support@vitalintegrators.com">Contact</a>
          <span>·</span>
          <a href="/privacy">Privacy</a>
          <span>·</span>
          <a href="/terms">Terms</a>
        </div>
      </footer>
    </div>
  );
}

function StepCard({ num, Icon, title, body }) {
  return (
    <div className="step-card">
      <div className="step-card__num">{num}</div>
      <div className="step-card__icon"><Icon size={22} /></div>
      <div className="step-card__title">{title}</div>
      <div className="step-card__body">{body}</div>
    </div>
  );
}

function FeatureCard({ Icon, title, body }) {
  return (
    <div className="card">
      <div className="tile__icon" style={{ marginBottom: 10 }}>
        <Icon size={20} />
      </div>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{title}</div>
      <div className="muted">{body}</div>
    </div>
  );
}

function FaqItem({ q, a }) {
  return (
    <details className="faq">
      <summary className="faq__q">{q}</summary>
      <div className="faq__a">{a}</div>
    </details>
  );
}
